import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  DEFAULT_PAGE_SIZE,
  apiParamsFromState,
  defaultUrlFilters,
  filtersFromSearch,
  pageFromSearch,
  urlParamsFromState,
} from './queueParams';

const useQueueUrlState = ({
  history,
  location,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const lastPushedSearchRef = useRef(null);
  const previousLocationSearchRef = useRef(location.search || '');

  const locationSearch = location.search || '';
  const externallyNavigated = previousLocationSearchRef.current !== locationSearch && lastPushedSearchRef.current !== locationSearch;
  const effectiveSearchTerm = externallyNavigated ? '' : searchTerm;
  const urlFilters = useMemo(() => filtersFromSearch(location.search), [location.search]);
  const urlPagination = useMemo(() => pageFromSearch(location.search), [location.search]);
  const requestParams = useMemo(
    () => apiParamsFromState(urlFilters, effectiveSearchTerm, urlPagination),
    [urlFilters, effectiveSearchTerm, urlPagination],
  );
  const requestKey = useMemo(() => JSON.stringify(requestParams), [requestParams]);

  const pushUrl = (nextUrlFilters, nextPagination) => {
    const params = new URLSearchParams(urlParamsFromState(nextUrlFilters, nextPagination));
    params.set('type', 'Follow Up');
    const nextSearch = `?${params.toString()}`;

    if (nextSearch === locationSearch) return;

    lastPushedSearchRef.current = nextSearch;
    history.replace(`${location.pathname}${nextSearch}`);
  };

  const updateFilters = (changes = {}) => {
    const { search, ...urlFilterChanges } = changes;
    const hasSearchChange = Object.prototype.hasOwnProperty.call(changes, 'search');
    const hasUrlFilterChanges = Object.keys(urlFilterChanges).length > 0;
    const nextSearchTerm = hasSearchChange ? search : searchTerm;
    const nextUrlFilters = { ...urlFilters, ...urlFilterChanges };
    const nextPagination = { ...urlPagination, current: 1 };

    if (hasSearchChange && nextSearchTerm === searchTerm && !hasUrlFilterChanges) return;
    if (hasSearchChange && nextSearchTerm !== searchTerm) setSearchTerm(nextSearchTerm);
    pushUrl(nextUrlFilters, nextPagination);
  };

  const handleClear = () => {
    const nextUrlFilters = { ...defaultUrlFilters };
    const nextPagination = {
      ...urlPagination,
      current: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    };
    setSearchTerm('');
    pushUrl(nextUrlFilters, nextPagination);
  };

  const handleTableChange = (nextPagination) => {
    const updatedPagination = {
      current: nextPagination.current,
      pageSize: nextPagination.pageSize,
    };
    pushUrl(urlFilters, updatedPagination);
  };

  useEffect(() => {
    if (lastPushedSearchRef.current === locationSearch) {
      lastPushedSearchRef.current = null;
      previousLocationSearchRef.current = locationSearch;
      return;
    }

    if (previousLocationSearchRef.current !== locationSearch) setSearchTerm('');
    previousLocationSearchRef.current = locationSearch;
  }, [locationSearch]);

  return {
    filters: { ...urlFilters, search: effectiveSearchTerm },
    handleClear,
    handleTableChange,
    requestKey,
    requestParams,
    updateFilters,
    urlFilters,
    urlPagination,
  };
};

export default useQueueUrlState;
